import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePortalSlug } from "@/contexts/PortalDomainContext";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Recycle, SignOut, UserCircle, Receipt, Calendar, Pause, Play, X, Plus, ArrowRight, MapPin } from "@phosphor-icons/react";
import AbfallPanel from "@/components/AbfallPanel";
import { resolveLogoUrl } from "@/lib/branding";
import CustomerVasAndOrdersPanel from "@/components/CustomerVasAndOrdersPanel";
import usePageTitle from "@/lib/usePageTitle";

export default function CustomerAccount() {
  const slug = usePortalSlug();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("overview");
  usePageTitle("Mein Konto", data?.tenant?.name);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      sessionStorage.setItem("tonneva_post_login", `/portal/${slug}/account`);
      sessionStorage.setItem("tonneva_tenant_slug", slug);
      navigate(`/portal/${slug}/login`);
      return;
    }
    if (user.role !== "customer") {
      // Logged in but wrong role — back to portal landing
      navigate(`/portal/${slug}`);
      return;
    }
    refresh();
  }, [user, loading, slug, navigate]);

  const refresh = async () => {
    try {
      const r = await api.get("/customer/me");
      setData(r.data);
      const b = r.data.tenant?.branding || {};
      document.documentElement.style.setProperty("--tenant-primary", b.primary_color || "#2563EB");
      document.documentElement.style.setProperty("--tenant-secondary", b.secondary_color || "#0EA5E9");
      document.documentElement.style.setProperty("--tenant-font", `"${b.font_family || "Outfit"}", sans-serif`);
      // Dynamically load tenant's Google Font
      if (b.font_family) {
        const id = "tonneva-tenant-font";
        let link = document.getElementById(id);
        if (!link) {
          link = document.createElement("link");
          link.id = id; link.rel = "stylesheet";
          document.head.appendChild(link);
        }
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(b.font_family)}:wght@400;500;600;700;800&display=swap`;
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Fehler beim Laden");
    }
  };

  if (loading || !data) return <div className="min-h-screen flex items-center justify-center" style={{ fontFamily: "var(--tenant-font)" }}>Lade…</div>;
  if (err) return <div className="min-h-screen flex items-center justify-center text-red-500">{err}</div>;

  const { customer, tenant, subscriptions, jobs, invoices, bins } = data;
  const b = tenant?.branding || {};
  const upcoming = jobs.filter((j) => j.status !== "done").slice(0, 5);
  const activeSubs = subscriptions.filter((s) => s.status === "active");
  const eur = (c) => `€${(c / 100).toFixed(2)}`;

  return (
    <div className="min-h-screen" style={{ background: "var(--tenant-surface, #f9fafb)", fontFamily: "var(--tenant-font)" }}>
      <header className="bg-white border-b border-black/5 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={`/portal/${slug}`} className="flex items-center gap-2.5">
            {b.logo_url ? <img src={resolveLogoUrl(b.logo_url)} alt={tenant.name} className="h-8" /> :
              <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: b.primary_color }}><Recycle size={18} weight="bold" className="text-white" /></div>}
            <div>
              <div className="font-bold tracking-tight text-sm">{tenant.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Mein Konto</div>
            </div>
          </Link>
          <div className="flex items-center gap-4 text-xs">
            <div className="hidden sm:flex items-center gap-2">
              {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" /> : <UserCircle size={26} weight="duotone" className="text-zinc-400" />}
              <span className="text-zinc-700">{user.name}</span>
            </div>
            <button onClick={logout} className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-900" data-testid="customer-logout-btn">
              <SignOut size={14} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="account-greeting">Hallo, {customer.first_name}! 👋</h1>
          <p className="text-zinc-500 mt-1 text-sm">Verwalten Sie Ihre Tonnenreinigung — alles an einem Ort.</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Stat icon={<Recycle size={14} weight="bold" />} label="Aktive Abos" value={activeSubs.length} />
          <Stat icon={<Calendar size={14} weight="bold" />} label="Anstehend" value={upcoming.length} />
          <Stat icon={<Receipt size={14} weight="bold" />} label="Rechnungen" value={invoices.length} />
          <Stat icon={<UserCircle size={14} weight="bold" />} label="Tonnen" value={bins.length} />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-black/5 rounded-full p-1">
            <TabsTrigger value="overview" className="rounded-full px-4 py-1.5 text-xs data-[state=active]:text-white" style={tab === "overview" ? { background: b.primary_color, color: "#fff" } : {}} data-testid="acc-tab-overview">Übersicht</TabsTrigger>
            <TabsTrigger value="subscriptions" className="rounded-full px-4 py-1.5 text-xs data-[state=active]:text-white" style={tab === "subscriptions" ? { background: b.primary_color, color: "#fff" } : {}} data-testid="acc-tab-subs">Abos</TabsTrigger>
            <TabsTrigger value="invoices" className="rounded-full px-4 py-1.5 text-xs data-[state=active]:text-white" style={tab === "invoices" ? { background: b.primary_color, color: "#fff" } : {}} data-testid="acc-tab-invoices">Rechnungen</TabsTrigger>
            <TabsTrigger value="extras" className="rounded-full px-4 py-1.5 text-xs data-[state=active]:text-white" style={tab === "extras" ? { background: b.primary_color, color: "#fff" } : {}} data-testid="acc-tab-extras">Extras</TabsTrigger>
            <TabsTrigger value="profile" className="rounded-full px-4 py-1.5 text-xs data-[state=active]:text-white" style={tab === "profile" ? { background: b.primary_color, color: "#fff" } : {}} data-testid="acc-tab-profile">Profil</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2 bg-white rounded-2xl border border-black/5 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2"><Calendar size={18} weight="bold" /> Anstehende Termine</h3>
                  {upcoming.length > 0 && (
                    <button
                      onClick={async () => {
                        try {
                          const r = await api.get("/customer/me/cleaning.ics", { responseType: "blob" });
                          const url = URL.createObjectURL(r.data);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = "tonneva-reinigungstermine.ics";
                          document.body.appendChild(a);
                          a.click();
                          setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
                        } catch (e) { console.error(e); }
                      }}
                      className="text-xs font-medium px-3 py-1.5 rounded-full border border-black/10 hover:bg-zinc-50 inline-flex items-center gap-1.5"
                      data-testid="download-cleaning-ics"
                    >
                      <Calendar size={12} weight="bold" /> Als Kalender (.ics)
                    </button>
                  )}
                </div>
                {upcoming.length === 0 ? (
                  <div className="text-center py-12 text-zinc-400 text-sm">
                    Keine Termine geplant.
                    <div className="mt-4">
                      <Link to={`/portal/${slug}/book`}>
                        <Button className="text-white rounded-full" style={{ background: b.primary_color }} data-testid="book-new-cta">
                          <Plus size={14} weight="bold" className="mr-1" /> Neue Reinigung buchen
                        </Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {upcoming.map((j) => (
                      <div key={j.job_id} className="flex items-center justify-between p-3 rounded-lg" style={{ background: "var(--tonneva-gradient-soft)" }}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-md flex items-center justify-center text-white text-xs font-bold" style={{ background: b.primary_color }}>
                            {new Date(j.scheduled_date).getDate()}
                          </div>
                          <div>
                            <div className="text-sm font-semibold">{new Date(j.scheduled_date).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "long" })}</div>
                            <div className="text-xs text-zinc-500">Status: {j.status}</div>
                          </div>
                        </div>
                        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: b.primary_color }}>{j.status === "in_progress" ? "Heute" : "Geplant"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl border border-black/5 p-6">
                <h3 className="font-bold text-lg mb-4">Schnellaktion</h3>
                <Link to={`/portal/${slug}/book`}>
                  <Button className="w-full text-white rounded-full h-11" style={{ background: b.primary_color }} data-testid="quick-book-btn">
                    <Plus size={14} weight="bold" className="mr-1.5" /> Neue Buchung
                  </Button>
                </Link>
                <div className="mt-6 pt-6 border-t border-black/5 text-sm">
                  <div className="flex items-start gap-2 mb-3">
                    <MapPin size={16} weight="bold" className="mt-0.5 flex-shrink-0" style={{ color: b.primary_color }} />
                    <div className="text-xs text-zinc-600">
                      {customer.location?.street}<br />
                      {customer.location?.zip} {customer.location?.city}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <AbfallPanel brandColor={b.primary_color} />
            </div>
          </TabsContent>

          <TabsContent value="subscriptions" className="mt-6">
            <div className="bg-white rounded-2xl border border-black/5">
              <div className="px-5 py-3 border-b border-black/5 flex items-center justify-between">
                <h3 className="font-bold text-lg">Meine Abos</h3>
                <Link to={`/portal/${slug}/book`}>
                  <Button size="sm" className="rounded-full text-white text-xs" style={{ background: b.primary_color }} data-testid="add-sub-btn">
                    <Plus size={12} weight="bold" className="mr-1" /> Neues Abo
                  </Button>
                </Link>
              </div>
              {subscriptions.length === 0 ? (
                <div className="p-12 text-center text-zinc-400 text-sm">Keine Abos vorhanden</div>
              ) : (
                <div className="divide-y divide-black/5">
                  {subscriptions.map((s) => (
                    <SubRow key={s.subscription_id} sub={s} brandColor={b.primary_color} onRefresh={refresh} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="invoices" className="mt-6">
            <div className="bg-white rounded-2xl border border-black/5">
              <div className="px-5 py-3 border-b border-black/5"><h3 className="font-bold text-lg">Rechnungen</h3></div>
              {invoices.length === 0 ? (
                <div className="p-12 text-center text-zinc-400 text-sm">Keine Rechnungen vorhanden</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-left text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr><th className="px-5 py-2">Nummer</th><th className="px-5 py-2">Datum</th><th className="px-5 py-2">Betrag</th><th className="px-5 py-2">Status</th></tr>
                  </thead>
                  <tbody>
                    {invoices.map((i) => (
                      <tr key={i.invoice_id} className="border-t border-black/5" data-testid={`acc-invoice-${i.invoice_id}`}>
                        <td className="px-5 py-3 font-mono">{i.number}</td>
                        <td className="px-5 py-3 text-zinc-600">{new Date(i.created_at).toLocaleDateString("de-DE")}</td>
                        <td className="px-5 py-3 font-mono font-medium">{eur(i.total_cents)}</td>
                        <td className="px-5 py-3"><span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: i.status === "paid" ? "#16A34A" : b.primary_color }}>{i.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <ProfilePanel customer={customer} onSaved={refresh} brandColor={b.primary_color} />
          </TabsContent>

          <TabsContent value="extras" className="mt-6">
            <CustomerVasAndOrdersPanel brandColor={b.primary_color} subscriptions={subscriptions} onRefresh={refresh} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const Stat = ({ icon, label, value }) => (
  <div className="bg-white rounded-xl border border-black/5 p-4">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">{icon} {label}</div>
    <div className="font-mono text-2xl font-medium tracking-tight">{value}</div>
  </div>
);

const SubRow = ({ sub, brandColor, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const setStatus = async (status) => {
    try { await api.patch(`/customer/subscriptions/${sub.subscription_id}`, { status }); onRefresh(); } catch (e) { alert(e.message); }
  };
  const skipNext = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.post(`/customer/subscriptions/${sub.subscription_id}/skip-next`);
      setMsg(`Termin am ${new Date(r.data.skipped_date).toLocaleDateString("de-DE")} pausiert`);
      onRefresh();
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!confirm("Abo wirklich endgültig kündigen? Alle geplanten Reinigungen werden abgesagt.")) return;
    setBusy(true); setErr(null);
    try {
      await api.patch(`/customer/subscriptions/${sub.subscription_id}`, { status: "canceled", cancel_reason: "customer_self_service" });
      onRefresh();
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const eur = (c) => `€${(c / 100).toFixed(2)}`;
  const statusColor = sub.status === "active" ? "#16A34A" : sub.status === "paused" ? "#F59E0B" : "#6B7280";
  const statusLabel = { active: "Aktiv", paused: "Pausiert", canceled: "Gekündigt" }[sub.status] || sub.status;

  return (
    <div className="p-5" data-testid={`acc-sub-${sub.subscription_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-semibold">{sub.plan_type.replace("_", " ").toUpperCase()}</div>
          <div className="text-xs text-zinc-500 mt-1">Frequenz: {sub.frequency} · Preis pro Reinigung: <span className="font-mono">{eur(sub.price_per_service_cents)}</span></div>
          {sub.next_service_date && <div className="text-xs text-zinc-500">Nächster Termin: {new Date(sub.next_service_date).toLocaleDateString("de-DE")}</div>}
          {sub.paused_until && <div className="text-xs text-amber-600">Pausiert bis: {new Date(sub.paused_until).toLocaleDateString("de-DE")}</div>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full text-white" style={{ background: statusColor }}>{statusLabel}</span>
          {sub.status === "active" && (
            <Button size="sm" variant="outline" className="rounded-full text-xs h-8" onClick={skipNext} disabled={busy} data-testid={`skip-next-${sub.subscription_id}`}>
              <Pause size={12} weight="bold" className="mr-1" /> Nächsten Termin pausieren
            </Button>
          )}
          {sub.status === "paused" && (
            <Button size="sm" variant="outline" className="rounded-full text-xs h-8" onClick={() => setStatus("active")} disabled={busy} data-testid={`resume-sub-${sub.subscription_id}`}>
              <Play size={12} weight="bold" className="mr-1" /> Aktivieren
            </Button>
          )}
          {sub.status !== "canceled" && (
            <Button size="sm" variant="ghost" className="rounded-full text-xs h-8 text-red-500 hover:bg-red-50" onClick={cancel} disabled={busy} data-testid={`cancel-sub-${sub.subscription_id}`}>
              <X size={12} weight="bold" className="mr-1" /> Kündigen
            </Button>
          )}
        </div>
      </div>
      {msg && <div className="mt-3 text-xs text-emerald-700 bg-emerald-50 rounded-md p-2" data-testid={`sub-msg-${sub.subscription_id}`}>{msg}</div>}
      {err && <div className="mt-3 text-xs text-red-600 bg-red-50 rounded-md p-2" data-testid={`sub-err-${sub.subscription_id}`}>{err}</div>}
      <p className="mt-3 text-[10px] text-zinc-400">Hinweis: Termine können nur bis 23:59 Uhr am Vortag pausiert werden. Für längere Pausen (mehrere Wochen) wenden Sie sich bitte an unseren Kundenservice.</p>
    </div>
  );
};

function ProfilePanel({ customer, onSaved, brandColor }) {
  const [f, setF] = useState({
    type: customer.type || "private",
    first_name: customer.first_name || "",
    last_name: customer.last_name || "",
    company_name: customer.company_name || "",
    email: customer.email || "",
    phone: customer.phone || "",
    location: {
      street: customer.location?.street || "",
      zip: customer.location?.zip || "",
      city: customer.location?.city || "",
    },
  });
  const save = async () => {
    try { await api.patch("/customer/me", f); onSaved(); alert("Profil gespeichert!"); } catch (e) { alert(e.message); }
  };
  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6 max-w-2xl">
      <h3 className="font-bold text-lg mb-4">Persönliche Daten</h3>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Vorname</Label><Input className="h-11 rounded-md" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="profile-first" /></div>
        <div><Label className="text-xs">Nachname</Label><Input className="h-11 rounded-md" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="profile-last" /></div>
        <div className="col-span-2"><Label className="text-xs">E-Mail</Label><Input className="h-11 rounded-md" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="profile-email" /></div>
        <div className="col-span-2"><Label className="text-xs">Telefon</Label><Input className="h-11 rounded-md" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="profile-phone" /></div>
        <div className="col-span-2 mt-2 pt-4 border-t border-black/5"><div className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2">Adresse</div></div>
        <div className="col-span-2"><Label className="text-xs">Straße & Hausnummer</Label><Input className="h-11 rounded-md" value={f.location.street} onChange={(e) => setF({ ...f, location: { ...f.location, street: e.target.value } })} data-testid="profile-street" /></div>
        <div><Label className="text-xs">PLZ</Label><Input className="h-11 rounded-md" value={f.location.zip} onChange={(e) => setF({ ...f, location: { ...f.location, zip: e.target.value } })} data-testid="profile-zip" /></div>
        <div><Label className="text-xs">Stadt</Label><Input className="h-11 rounded-md" value={f.location.city} onChange={(e) => setF({ ...f, location: { ...f.location, city: e.target.value } })} data-testid="profile-city" /></div>
      </div>
      <Button onClick={save} className="mt-6 rounded-full text-white px-6" style={{ background: brandColor }} data-testid="profile-save-btn">Speichern</Button>
    </div>
  );
}
